import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { Session, User, SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "../hooks/useAuth"; // Import the hook we just created

// Import the SignUpOptions interface or redefine it here
interface SignUpOptions {
  data?: {
    full_name?: string;
    [key: string]: any; // Allow other metadata properties
  };
  [key: string]: any; // Allow other options like redirectTo
}

// Define the shape of the context value
interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  error: Error | null;
  signInWithPassword: (
    email: string,
    password: string
  ) => Promise<{ user: User | null; error: any | null }>;
  signUp: (
    email: string,
    password: string,
    options?: SignUpOptions
  ) => Promise<{ user: User | null; error: any | null }>;
  signOut: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<{ error: any | null }>;
  signInWithGoogle: (
    intent?: "login" | "signup",
    returnTo?: string | null
  ) => Promise<void>;
  updateProfile: (data: { full_name?: string }) => Promise<{ error: any | null }>;
  supabase: SupabaseClient;
}

// Create the context with a default value (can be null or undefined initially)
// Using `null` here and checking in the consumer hook is a common pattern.
const AuthContext = createContext<AuthContextType | null>(null);

// Define the props for the AuthProvider
interface AuthProviderProps {
  children: ReactNode;
}

// Create the AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();

  // Use useMemo to prevent the context value from changing on every render
  // unless the auth state itself changes.
  // We cast the functions to the expected type for the context.
  const value = useMemo(
    () => ({
      ...auth,
      // Explicitly cast placeholder functions to match AuthContextType if necessary
      // Though their signatures should match if defined correctly in useAuth.
    }),
    [
      auth.session,
      auth.user,
      auth.loading,
      auth.error,
      auth.supabase,
      // Include function references if they might change identity, though typically stable
      auth.signInWithPassword,
      auth.signUp,
      auth.signOut,
      auth.sendPasswordResetEmail,
      auth.signInWithGoogle,
      auth.updateProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create a custom hook for easy context consumption
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    // This error means you tried to use the context outside of an AuthProvider
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
